import SortOutlinedIcon from '@mui/icons-material/SortOutlined';
import TablePagination from '@mui/material/TablePagination';
import {
  type ReactNode,
  type ChangeEvent,
  type MouseEvent,
  type ReactElement,
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type DailyHistorySortKey,
  type EntityDailyHistoryRow,
} from '@/features/shared/components/EntityDailyHistoryPanel.types';
import { EntityDailyHistoryRows } from '@/features/shared/components/EntityDailyHistoryPanelRows';
import { EntityDailyHistoryPanelSkeleton } from '@/features/shared/components/EntityDailyHistoryPanelSkeleton';
import { TABLE_GRID_HEADER_HEIGHT } from '@/features/shared/components/tableSystem/tableConstants';
import {
  TableSortPopover,
  type TableSortOption,
} from '@/features/shared/components/tableSystem/TableSortPopover';
import {
  buildToolbarIconButtonSx,
  buildToolbarIconCircleSx,
} from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import { isLightTheme } from '@/theme/semanticTokens';
import { s } from '@/theme/tokens';
import { Panel } from '@/ui/composites/Panel';
import { Stack } from '@/ui/layout/Stack';
import {
  Box,
  Cancel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Text,
  Tooltip,
  useTheme,
} from '@/ui/primitives';
import { dataGridChromeSx } from '@/ui/primitives/dataGridChrome';

type EntityDailyHistoryPanelProps = {
  title: string;
  dateRangeLabel?: string | null;
  entityLabel: string;
  activityLabel: 'executions' | 'builds';
  rows: EntityDailyHistoryRow[];
  loading?: boolean;
  noRowsText: string;
  defaultSortKey?: DailyHistorySortKey;
  defaultSortDirection?: 'asc' | 'desc';
  sortKey?: DailyHistorySortKey;
  sortDirection?: 'asc' | 'desc';
  sortOptions?: Array<TableSortOption<DailyHistorySortKey>>;
  onSortChange?: (sortKey: DailyHistorySortKey, sortDirection: 'asc' | 'desc') => void;
  getSortValue?: (row: EntityDailyHistoryRow, sortKey: DailyHistorySortKey) => string;
  page?: number;
  pageSize?: number;
  totalCount?: number;
  onPageChange?: (nextPage: number) => void;
  onPageSizeChange?: (nextPageSize: number) => void;
};

const DAILY_STATUS_BLOCK_SIZE = 18;
const DAILY_STATUS_ROW_HEIGHT = 30;
const HISTORY_PAGE_SIZE_OPTIONS = [5, 10, 25, 50] as const;
const HISTORY_DEFAULT_PAGE_SIZE = 5;
const HISTORY_DEFAULT_SORT_DIRECTION = 'asc' as const;
const HISTORY_TOOLBAR_TOOLTIP_SLOT_PROPS = {
  tooltip: {
    sx: {
      fontSize: 'subtitle1.fontSize',
      lineHeight: 1.4,
    },
  },
} as const;
const HEADER_SEPARATOR_SX = {
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    width: '1px',
    height: `calc(${TABLE_GRID_HEADER_HEIGHT}px - ${s('md')})`,
    backgroundColor: 'divider',
  },
} as const;
const HEADER_TEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(15),
  fontWeight: 600,
  lineHeight: 1.4,
} as const;
const PANEL_TITLE_TEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(19),
  fontWeight: 600,
  lineHeight: 1.4,
} as const;
const HEADER_SUBTEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(13),
  fontWeight: 500,
  lineHeight: 1.2,
  color: 'text.secondary',
} as const;
const ROW_NAME_TEXT_SX = {
  fontSize: (theme: { typography: { pxToRem: (value: number) => string } }) =>
    theme.typography.pxToRem(15),
  lineHeight: 1.4,
} as const;
const HISTORY_DAY_HEADER_MIN_LABEL_WIDTH_PX = 52;
const HISTORY_NAME_COLUMN_MIN_WIDTH_PX = 240;
const HISTORY_NAME_COLUMN_MAX_WIDTH_RATIO = 0.6;
const HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX = 10;

const shouldShowDayHeaderLabel = (index: number, lastIndex: number, interval: number): boolean =>
  index === 0 || index === lastIndex || index % interval === 0;

const buildDayHeaderLabel = (
  dayLabel: ReactNode,
  index: number,
  lastIndex: number,
  interval: number,
): ReactNode => (shouldShowDayHeaderLabel(index, lastIndex, interval) ? dayLabel : '\u00A0');

export const EntityDailyHistoryPanel = ({
  title,
  dateRangeLabel = null,
  entityLabel,
  activityLabel,
  rows,
  loading = false,
  noRowsText,
  defaultSortKey,
  defaultSortDirection = HISTORY_DEFAULT_SORT_DIRECTION,
  sortKey: controlledSortKey,
  sortDirection: controlledSortDirection,
  sortOptions,
  onSortChange: onControlledSortChange,
  getSortValue,
  page: controlledPage,
  pageSize: controlledPageSize,
  totalCount: controlledTotalCount,
  onPageChange: onControlledPageChange,
  onPageSizeChange: onControlledPageSizeChange,
}: EntityDailyHistoryPanelProps): ReactElement => {
  const days = rows[0]?.dailyHistory ?? [];
  const theme = useTheme();
  const toolbarControlSize = 32;
  const toolbarInactiveIconColor = isLightTheme(theme) ? 'text.secondary' : 'common.white';
  const toolbarIconButtonSx = buildToolbarIconButtonSx(
    toolbarInactiveIconColor,
    toolbarControlSize,
  );
  const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
  const [hoverTarget, setHoverTarget] = useState<'sort' | 'reset' | null>(null);
  const resolvedDefaultSortKey = defaultSortKey ?? 'name';
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(() => new Set());
  const [internalPage, setInternalPage] = useState(0);
  const [internalPageSize, setInternalPageSize] = useState(HISTORY_DEFAULT_PAGE_SIZE);
  const dayHeaderGridRef = useRef<HTMLDivElement | null>(null);
  const panelTableContainerRef = useRef<HTMLDivElement | null>(null);
  const nameColumnHeaderCellRef = useRef<HTMLTableCellElement | null>(null);
  const [dayHeaderGridWidth, setDayHeaderGridWidth] = useState(0);
  const [resizedNameColumnWidthPx, setResizedNameColumnWidthPx] = useState<number | null>(null);
  const [internalSortKey, setInternalSortKey] =
    useState<DailyHistorySortKey>(resolvedDefaultSortKey);
  const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>(
    defaultSortDirection,
  );
  const isPaginationControlled =
    controlledPage !== undefined &&
    controlledPageSize !== undefined &&
    controlledTotalCount !== undefined &&
    onControlledPageChange !== undefined &&
    onControlledPageSizeChange !== undefined;
  const isSortControlled =
    controlledSortKey !== undefined &&
    controlledSortDirection !== undefined &&
    onControlledSortChange !== undefined;
  const page = isPaginationControlled ? controlledPage : internalPage;
  const pageSize = isPaginationControlled ? controlledPageSize : internalPageSize;
  const sortKey = isSortControlled ? controlledSortKey : internalSortKey;
  const sortDirection = isSortControlled ? controlledSortDirection : internalSortDirection;
  const resolvedSortOptions = sortOptions ?? [{ key: resolvedDefaultSortKey, label: entityLabel }];
  const hasActiveSort =
    sortKey !== resolvedDefaultSortKey || sortDirection !== defaultSortDirection;
  const preserveWindowScroll = (action: () => void): void => {
    if (typeof window === 'undefined') {
      action();
      return;
    }
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;
    action();
    window.requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
    });
  };
  const sortedRows = useMemo(() => {
    const resolveSortValue = (row: EntityDailyHistoryRow): string => {
      if (getSortValue) return getSortValue(row, sortKey);
      if (sortKey === 'prefixLabel') return row.prefixLabel ?? '';
      return row.name;
    };
    const sortRowList = (inputRows: EntityDailyHistoryRow[]): EntityDailyHistoryRow[] => {
      const nextRows = [...inputRows];
      nextRows.sort((left, right) => {
        const leftValue = resolveSortValue(left);
        const rightValue = resolveSortValue(right);
        const primaryComparison = leftValue.localeCompare(rightValue, undefined, {
          sensitivity: 'base',
        });
        if (primaryComparison !== 0) {
          return sortDirection === 'asc' ? primaryComparison : primaryComparison * -1;
        }
        const leftPrefix = left.prefixLabel ?? '';
        const rightPrefix = right.prefixLabel ?? '';
        const prefixComparison = leftPrefix.localeCompare(rightPrefix, undefined, {
          sensitivity: 'base',
        });
        if (prefixComparison !== 0) {
          return sortDirection === 'asc' ? prefixComparison : prefixComparison * -1;
        }
        const fallbackComparison = left.name.localeCompare(right.name, undefined, {
          sensitivity: 'base',
        });
        return sortDirection === 'asc' ? fallbackComparison : fallbackComparison * -1;
      });
      return nextRows.map((row) => ({
        ...row,
        childRows: row.childRows ? sortRowList(row.childRows) : undefined,
      }));
    };
    return sortRowList(rows);
  }, [getSortValue, rows, sortDirection, sortKey]);
  useEffect(() => {
    if (!isPaginationControlled) {
      setInternalPage(0);
    }
  }, [isPaginationControlled, sortDirection, sortKey]);
  const closeSortPopover = (): void => {
    setSortAnchorEl(null);
  };
  const handleSortButtonClick = (event: MouseEvent<HTMLButtonElement>): void => {
    setSortAnchorEl(event.currentTarget);
  };
  const handleSortChange = (
    nextSortKey: DailyHistorySortKey,
    nextSortDirection: 'asc' | 'desc',
  ): void => {
    preserveWindowScroll(() => {
      if (isSortControlled) {
        onControlledSortChange(nextSortKey, nextSortDirection);
      } else {
        setInternalSortKey(nextSortKey);
        setInternalSortDirection(nextSortDirection);
      }
      if (!isPaginationControlled) {
        setInternalPage(0);
      }
      closeSortPopover();
    });
  };
  const handleReset = (): void => {
    preserveWindowScroll(() => {
      if (isSortControlled) {
        onControlledSortChange(resolvedDefaultSortKey, defaultSortDirection);
      } else {
        setInternalSortKey(resolvedDefaultSortKey);
        setInternalSortDirection(defaultSortDirection);
      }
      if (!isPaginationControlled) {
        setInternalPage(0);
      }
      closeSortPopover();
    });
  };
  useEffect(() => {
    if (isPaginationControlled) {
      return;
    }
    const maxPage = Math.max(0, Math.ceil(sortedRows.length / pageSize) - 1);
    if (page > maxPage) {
      setInternalPage(maxPage);
    }
  }, [isPaginationControlled, page, pageSize, sortedRows.length]);
  const paginatedRows = useMemo(() => {
    if (isPaginationControlled) {
      return sortedRows;
    }
    const startIndex = page * pageSize;
    return sortedRows.slice(startIndex, startIndex + pageSize);
  }, [isPaginationControlled, page, pageSize, sortedRows]);
  const defaultNameColumnWidth = useMemo(() => {
    const hasExpandableRows = rows.some((row) =>
      Boolean(row.childRows && row.childRows.length > 0),
    );
    const longestTopLevelLabelLength = rows.reduce((maxLength, row) => {
      const prefix = row.prefixLabel ? `${row.prefixLabel} / ` : '';
      const nextLength = `${prefix}${row.name}`.length;
      return Math.max(maxLength, nextLength);
    }, entityLabel.length);
    return `calc(${String(longestTopLevelLabelLength + 2)}ch + ${s('xl')}${hasExpandableRows ? ' + 32px' : ''})`;
  }, [entityLabel.length, rows]);
  const clampNameColumnWidth = useCallback((widthPx: number): number => {
    const panelWidth = panelTableContainerRef.current?.clientWidth ?? 0;
    const maxWidth =
      panelWidth > 0
        ? Math.max(
            HISTORY_NAME_COLUMN_MIN_WIDTH_PX,
            Math.floor(panelWidth * HISTORY_NAME_COLUMN_MAX_WIDTH_RATIO),
          )
        : widthPx;
    return Math.min(Math.max(widthPx, HISTORY_NAME_COLUMN_MIN_WIDTH_PX), maxWidth);
  }, []);
  const nameColumnWidth =
    resizedNameColumnWidthPx === null
      ? defaultNameColumnWidth
      : `${String(clampNameColumnWidth(resizedNameColumnWidthPx))}px`;
  const toggleRowExpansion = (rowId: string): void => {
    preserveWindowScroll(() => {
      setExpandedRowIds((current) => {
        const next = new Set(current);
        if (next.has(rowId)) {
          next.delete(rowId);
        } else {
          next.add(rowId);
        }
        return next;
      });
    });
  };
  const handlePageChange = (_event: unknown, nextPage: number): void => {
    preserveWindowScroll(() => {
      if (isPaginationControlled) {
        onControlledPageChange(nextPage);
        return;
      }
      setInternalPage(nextPage);
    });
  };
  const handleRowsPerPageChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ): void => {
    const nextPageSize = Number.parseInt(event.target.value, 10);
    preserveWindowScroll(() => {
      if (isPaginationControlled) {
        onControlledPageSizeChange(nextPageSize);
        return;
      }
      setInternalPageSize(nextPageSize);
      setInternalPage(0);
    });
  };
  const formatHeaderDay = (value: Date): string =>
    new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
    }).format(value);
  const updateDayHeaderGridWidth = useCallback((): void => {
    const nextWidth = dayHeaderGridRef.current?.clientWidth ?? 0;
    setDayHeaderGridWidth((currentWidth) =>
      currentWidth === nextWidth ? currentWidth : nextWidth,
    );
  }, []);
  useEffect(() => {
    updateDayHeaderGridWidth();
    const gridElement = dayHeaderGridRef.current;
    const panelTableContainerElement = panelTableContainerRef.current;
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(() => {
      updateDayHeaderGridWidth();
      setResizedNameColumnWidthPx((currentWidth) =>
        currentWidth === null ? currentWidth : clampNameColumnWidth(currentWidth),
      );
    });
    if (gridElement) {
      observer.observe(gridElement);
    }
    if (panelTableContainerElement) {
      observer.observe(panelTableContainerElement);
    }
    return () => {
      observer.disconnect();
    };
  }, [clampNameColumnWidth, updateDayHeaderGridWidth]);
  const handleNameColumnResizeStart = (event: ReactPointerEvent<HTMLButtonElement>): void => {
    const headerCellElement = nameColumnHeaderCellRef.current;
    if (!headerCellElement) return;
    const startX = event.clientX;
    const startWidth = headerCellElement.getBoundingClientRect().width;
    const pointerId = event.pointerId;
    const targetElement = event.currentTarget;
    targetElement.setPointerCapture(pointerId);
    const handlePointerMove = (moveEvent: PointerEvent): void => {
      const nextWidth = startWidth + (moveEvent.clientX - startX);
      setResizedNameColumnWidthPx(clampNameColumnWidth(nextWidth));
    };
    const handlePointerUp = (): void => {
      targetElement.releasePointerCapture(pointerId);
      targetElement.removeEventListener('pointermove', handlePointerMove);
      targetElement.removeEventListener('pointerup', handlePointerUp);
      targetElement.removeEventListener('pointercancel', handlePointerUp);
    };
    targetElement.addEventListener('pointermove', handlePointerMove);
    targetElement.addEventListener('pointerup', handlePointerUp);
    targetElement.addEventListener('pointercancel', handlePointerUp);
  };
  const dayHeaderInterval = useMemo(() => {
    if (days.length <= 1 || dayHeaderGridWidth <= 0) return 1;
    const dayColumnWidth = dayHeaderGridWidth / days.length;
    if (dayColumnWidth >= HISTORY_DAY_HEADER_MIN_LABEL_WIDTH_PX) return 1;
    return Math.max(1, Math.ceil(HISTORY_DAY_HEADER_MIN_LABEL_WIDTH_PX / dayColumnWidth));
  }, [dayHeaderGridWidth, days.length]);

  return (
    <Panel
      padding="md"
      gap="none"
      contentGap="none"
      headerGap="none"
      headerSlot={
        <Box
          sx={{
            width: '100%',
            minWidth: 0,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap="sm"
            sx={{ minHeight: '45px', height: '45px' }}
          >
            <Box
              sx={{
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                pl: s('md'),
              }}
            >
              <Text role="bodyStrong" sx={[PANEL_TITLE_TEXT_SX, { color: 'text.primary' }]}>
                {title}
              </Text>
            </Box>
            <Stack direction="row" alignItems="center" gap="sm" sx={{ ml: 'auto', pr: s('md') }}>
              {dateRangeLabel ? (
                <Text
                  role="bodyMuted"
                  sx={{
                    fontSize: (muiTheme) => muiTheme.typography.subtitle1.fontSize,
                    lineHeight: (muiTheme) => muiTheme.typography.subtitle1.lineHeight,
                    fontWeight: (muiTheme) => muiTheme.typography.fontWeightMedium,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {dateRangeLabel}
                </Text>
              ) : null}
              <Tooltip
                title="Sort"
                arrow
                enterDelay={250}
                slotProps={HISTORY_TOOLBAR_TOOLTIP_SLOT_PROPS}
              >
                <IconButton
                  size="medium"
                  aria-label="Open sort"
                  onClick={handleSortButtonClick}
                  onMouseEnter={() => {
                    setHoverTarget('sort');
                  }}
                  onMouseLeave={() => {
                    setHoverTarget(null);
                  }}
                  sx={[
                    toolbarIconButtonSx,
                    { color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor },
                  ]}
                >
                  <Box sx={buildToolbarIconCircleSx(hoverTarget === 'sort')}>
                    <SortOutlinedIcon
                      sx={{
                        color: hasActiveSort ? 'primary.main' : toolbarInactiveIconColor,
                        width: '22px !important',
                        height: '22px !important',
                      }}
                    />
                  </Box>
                </IconButton>
              </Tooltip>
              <Tooltip
                title="Reset"
                arrow
                enterDelay={250}
                slotProps={HISTORY_TOOLBAR_TOOLTIP_SLOT_PROPS}
              >
                <Box
                  component="span"
                  sx={{ display: 'flex', alignItems: 'center', height: `${toolbarControlSize}px` }}
                >
                  <IconButton
                    size="medium"
                    aria-label="Reset history sorting"
                    onClick={handleReset}
                    disabled={!hasActiveSort}
                    onMouseEnter={() => {
                      setHoverTarget('reset');
                    }}
                    onMouseLeave={() => {
                      setHoverTarget(null);
                    }}
                    sx={[toolbarIconButtonSx, hasActiveSort ? { color: 'primary.main' } : {}]}
                  >
                    <Box sx={buildToolbarIconCircleSx(hoverTarget === 'reset' && hasActiveSort)}>
                      <Cancel sx={{ width: '22px !important', height: '22px !important' }} />
                    </Box>
                  </IconButton>
                </Box>
              </Tooltip>
            </Stack>
          </Stack>
        </Box>
      }
      sx={{ backgroundColor: 'background.paper' }}
    >
      <Box ref={panelTableContainerRef} sx={{ overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ overflow: 'hidden' }}>
            <EntityDailyHistoryPanelSkeleton
              dayCount={days.length > 0 ? days.length : 7}
              rowCount={pageSize}
              nameColumnWidth={nameColumnWidth}
              rowHeight={DAILY_STATUS_ROW_HEIGHT}
              blockSize={DAILY_STATUS_BLOCK_SIZE}
              headerHeight={TABLE_GRID_HEADER_HEIGHT + 14}
            />
          </Box>
        ) : rows.length === 0 || days.length === 0 ? (
          <Box sx={{ px: s('md'), pb: s('sm') }}>
            <Text role="body" sx={{ color: 'text.secondary' }}>
              {noRowsText}
            </Text>
          </Box>
        ) : (
          <Table
            aria-label={title}
            sx={{
              tableLayout: 'auto',
              width: '100%',
              '& .MuiTableBody-root > .MuiTableRow-root:first-of-type > .MuiTableCell-root': {
                pt: s('sm'),
              },
              '& .MuiTableBody-root > .MuiTableRow-root:last-of-type > .MuiTableCell-root': {
                pb: s('sm'),
              },
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  height: `${TABLE_GRID_HEADER_HEIGHT + 14}px`,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <TableCell
                  ref={nameColumnHeaderCellRef}
                  sx={[
                    {
                      width: nameColumnWidth,
                      minWidth: nameColumnWidth,
                      maxWidth: nameColumnWidth,
                      px: s('md'),
                      pt: s('md'),
                      pb: s('none'),
                      verticalAlign: 'top',
                      borderBottom: 'none',
                      position: 'relative',
                    },
                    HEADER_SEPARATOR_SX,
                  ]}
                >
                  <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                    {entityLabel}
                  </Text>
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Resize ${entityLabel} column`}
                    onPointerDown={handleNameColumnResizeStart}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX / -2)}px`,
                      width: `${String(HISTORY_NAME_COLUMN_RESIZE_HANDLE_WIDTH_PX)}px`,
                      height: '100%',
                      p: s('none'),
                      border: 'none',
                      backgroundColor: 'transparent',
                      cursor: 'col-resize',
                      zIndex: 1,
                      touchAction: 'none',
                      '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: s('sm'),
                        bottom: s('sm'),
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '2px',
                        borderRadius: '999px',
                        backgroundColor: 'divider',
                        opacity: 0,
                        transition: (muiTheme) =>
                          muiTheme.transitions.create('opacity', {
                            duration: muiTheme.transitions.duration.shortest,
                          }),
                      },
                      '&:hover::after, &:focus-visible::after, &:active::after': {
                        opacity: 1,
                      },
                    }}
                  />
                </TableCell>
                <TableCell
                  sx={{
                    px: s('md'),
                    pt: s('md'),
                    pb: s('none'),
                    verticalAlign: 'top',
                    borderBottom: 'none',
                  }}
                >
                  <Stack gap="sm" sx={{ minWidth: 0 }}>
                    <Text role="body" sx={[HEADER_TEXT_SX, { color: 'text.primary' }]}>
                      Status
                    </Text>
                    <Box
                      ref={dayHeaderGridRef}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(${String(days.length)}, minmax(0, 1fr))`,
                        columnGap: s('md'),
                        alignItems: 'center',
                        width: '100%',
                        minWidth: 0,
                        pb: s('none'),
                      }}
                    >
                      {days.map((day, index) => (
                        <Text
                          key={day.day.toISOString()}
                          role="body"
                          sx={[
                            HEADER_SUBTEXT_SX,
                            {
                              textAlign: 'left',
                              whiteSpace: 'nowrap',
                            },
                          ]}
                        >
                          {buildDayHeaderLabel(
                            formatHeaderDay(day.day),
                            index,
                            days.length - 1,
                            dayHeaderInterval,
                          )}
                        </Text>
                      ))}
                    </Box>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <EntityDailyHistoryRows
                rows={paginatedRows}
                expandedRowIds={expandedRowIds}
                onToggleRowExpansion={toggleRowExpansion}
                rowHeight={DAILY_STATUS_ROW_HEIGHT}
                blockSize={DAILY_STATUS_BLOCK_SIZE}
                nameColumnWidth={nameColumnWidth}
                rowNameTextSx={ROW_NAME_TEXT_SX}
                activityLabel={activityLabel}
              />
            </TableBody>
          </Table>
        )}
        {!loading && rows.length > 0 && days.length > 0 ? (
          <Box
            sx={{
              borderTop: '1px solid',
              borderColor: 'divider',
              overflow: 'hidden',
              '& .MuiTablePagination-displayedRows': {
                marginRight: s('none'),
              },
              '& .MuiTablePagination-actions': {
                marginLeft: s('xs'),
              },
            }}
          >
            <Box sx={dataGridChromeSx}>
              <TablePagination
                component="div"
                count={isPaginationControlled ? controlledTotalCount : sortedRows.length}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[...HISTORY_PAGE_SIZE_OPTIONS]}
              />
            </Box>
          </Box>
        ) : null}
      </Box>
      <TableSortPopover
        open={sortAnchorEl !== null}
        anchorEl={sortAnchorEl}
        onClose={closeSortPopover}
        sortKey={sortKey}
        sortDirection={sortDirection}
        sortOptions={resolvedSortOptions}
        onSortChange={handleSortChange}
        preserveWindowScroll={preserveWindowScroll}
      />
    </Panel>
  );
};

export type { EntityDailyHistoryRow } from '@/features/shared/components/EntityDailyHistoryPanel.types';
